/*
 * Plan-gate refusals from diet-api (SAAS-REVAMP/DESIGN.md §8.1). GraphQL errors arrive as
 * `{ message, statusCode, type, ...extra }` objects; REST refusals as a 402/423 JSON body `{ type, ... }`.
 *
 *   FEATURE_NOT_IN_PLAN  402  { feature }
 *   PLAN_LIMIT_REACHED   402  { limit, used, max }
 *   ACCOUNT_SUSPENDED    423
 *
 * They are never a session problem (see isAuthErrorFromGraphQL) and are reported to the root
 * instance as PLAN_ERROR_EVENT, which App.vue answers with the upgrade dialog.
 */
export const PLAN_ERROR_TYPES = ["FEATURE_NOT_IN_PLAN", "PLAN_LIMIT_REACHED", "ACCOUNT_SUSPENDED"];
export const PLAN_ERROR_EVENT = "plan-upgrade-required";

export function isPlanErrorType(type) {
  return PLAN_ERROR_TYPES.indexOf(type) > -1;
}

export function isPlanError(err) {
  return !!err && typeof err === "object" && isPlanErrorType(err.type);
}

/** The event payload: { type, feature, limit, used, max } (missing fields are null). */
export function planErrorDetail(err) {
  return {
    type: err.type,
    feature: typeof err.feature === "string" && err.feature ? err.feature : null,
    limit: typeof err.limit === "string" && err.limit ? err.limit : null,
    used: typeof err.used === "number" ? err.used : null,
    max: typeof err.max === "number" ? err.max : null,
  };
}

/**
 * Every plan-gate error an Apollo error carries: its GraphQL errors and, for a refusal the
 * server sent with a 4xx status, the errors in the network error's body.
 */
export function planErrorsOf(error) {
  if (!error || typeof error !== "object") return [];
  const found = (error.graphQLErrors || []).filter(isPlanError);
  const body = error.networkError && error.networkError.result;
  if (body && Array.isArray(body.errors)) body.errors.filter(isPlanError).forEach(err => found.push(err));
  return found;
}

let bus = null;
const reportedQueryErrors = new Set();

/** Forgets which query refusals were already shown (sign-out: the next session starts fresh). */
export function resetPlanErrorReports() {
  reportedQueryErrors.clear();
}

/** App.vue binds its root instance here; reports before that (or after unbinding) are dropped. */
export function bindPlanErrorBus(vm) {
  bus = vm || null;
}

/**
 * Raises the upgrade dialog for one refusal. A query refusal is shown once per type + feature per
 * page load (background queries would otherwise reopen it on every visit); a mutation — something
 * the user just did — always is.
 * @returns {boolean} whether the event was emitted
 */
export function reportPlanError(err, { operationType } = {}) {
  if (!bus || !isPlanError(err)) return false;
  const detail = planErrorDetail(err);
  if (operationType !== "mutation" && detail.type !== "ACCOUNT_SUSPENDED") {
    const key = detail.type + ":" + (detail.feature || detail.limit || "");
    if (reportedQueryErrors.has(key)) return false;
    reportedQueryErrors.add(key);
  }
  bus.$emit(PLAN_ERROR_EVENT, detail);
  return true;
}
